import { useStytchB2BClient } from "@stytch/react/b2b";
import axios from "axios";
import isArray from "lodash/isArray";
import isBoolean from "lodash/isBoolean";
import isEmpty from "lodash/isEmpty";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";
import CustomSnackbar from "../_src_shared/components/CustomSnackbar";
import { getValueFromMappedObject } from "../utils/objects/getValueFromMappedObject";

export const useAxiosHook = () => {
  const stytch = useStytchB2BClient();
  const tokens = stytch.session.getTokens();
  const session_token = tokens?.session_token;

  const [axiosPostIsLoading, setAxiosPostIsLoading] = useState(false);
  const [axiosGetIsLoading, setAxiosGetIsLoading] = useState(false);
  const [axiosSSEIsLoading, setAxiosSSEIsLoading] = useState(false);
  const [axiosSSEProgress, setAxiosSSEProgress] = useState(null);

  const [axiosError, setAxiosError] = useState(null);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const BASE_URL =
    process.env.REACT_APP_BACKEND_BASE_URL ||
    "https://rtl-backend.ambitioussmoke-43b0762d.eastus.azurecontainerapps.io";

  const ORDER_ENGINE_URL =
    process.env.REACT_APP_ORDER_ENGINE_URL ||
    "https://los-workflows.bluebay-5ecdbe39.eastus2.azurecontainerapps.io";

  const domainTypeMap = {
    default: BASE_URL,
    test: "https://test-integration-portal.wittysmoke-19071c7a.eastus.azurecontainerapps.io",
    errorTesting: "https://env-example.azurewebsites.net",
    resfin:
      "https://rtl-integration-portal.wittysmoke-19071c7a.eastus.azurecontainerapps.io",
    orderEngine: ORDER_ENGINE_URL,
  };

  // ----------------------------
  // logger helpers
  // ----------------------------
  const LOG_STYLES = {
    GET: {
      out: "background:#0b8f1a22;color:#0b8f1a;font-weight:600;padding:2px 6px;border-radius:4px;",
      in: "background:#0b8f1a44;color:#0b8f1a;font-weight:600;padding:2px 6px;border-radius:4px;",
      err: "background:#ff000022;color:#b00000;font-weight:600;padding:2px 6px;border-radius:4px;",
    },
    POST: {
      out: "background:#0a66ff22;color:#0a66ff;font-weight:600;padding:2px 6px;border-radius:4px;",
      in: "background:#0a66ff44;color:#0a66ff;font-weight:600;padding:2px 6px;border-radius:4px;",
      err: "background:#ff000022;color:#b00000;font-weight:600;padding:2px 6px;border-radius:4px;",
    },
  };

  const logRequest = ({ method, name, url, payload }) => {
    const m = method.toUpperCase();
    const styles = LOG_STYLES[m] || LOG_STYLES.GET;

    console.groupCollapsed(
      `%c${m} |>| ${name || "(unnamed request)"}`,
      styles.out,
    );
    console.log("URL:", url);
    if (payload !== undefined) console.log("Payload:", payload);
    console.groupEnd();
  };

  const logResponse = ({ method, name, data, isFile }) => {
    const m = method.toUpperCase();
    const styles = LOG_STYLES[m] || LOG_STYLES.GET;

    console.groupCollapsed(
      `%c${m} |<| ${name || "(unnamed request)"}`,
      styles.in,
    );
    console.log("Response:", isFile ? "File request handled" : data);
    console.groupEnd();
  };

  const logError = ({ method, name, err }) => {
    const m = method.toUpperCase();
    const styles = LOG_STYLES[m] || LOG_STYLES.GET;

    console.groupCollapsed(
      `%c${m} |<| ${name || "(unnamed request)"} (ERROR)`,
      styles.err,
    );
    console.error(err);
    console.groupEnd();
  };

  // ----------------------------
  // shared file handler
  // ----------------------------
  const handleFileBlob = ({
    blob,
    headers,
    fileDownload,
    fileDownloadName,
    openInNewTab,
  }) => {
    const fileBlobUrl = window.URL.createObjectURL(blob);

    const contentDisposition = headers?.["content-disposition"];
    let filename = fileDownloadName || "download";

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/["']/g, "");
      }
    }

    if (openInNewTab) {
      const newTab = window.open(fileBlobUrl, "_blank");
      if (!newTab) {
        console.warn("Unable to open new tab; check browser pop-up settings.");
      }
      return;
    }

    if (fileDownload) {
      const a = document.createElement("a");
      a.href = fileBlobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(fileBlobUrl);
    }
  };

  // ----------------------------
  // axios instances per domain
  // ----------------------------
  const axiosClients = useMemo(() => {
    const makeClient = (baseURL) =>
      axios.create({
        baseURL,
        headers: {
          Authorization: `Bearer ${session_token}`,
        },
      });

    return {
      default: makeClient(domainTypeMap.default),
      resfin: makeClient(domainTypeMap.resfin),
      errorTesting: makeClient(domainTypeMap.errorTesting),
      test: makeClient(domainTypeMap.test),
      orderEngine: makeClient(domainTypeMap.orderEngine),
    };
  }, [session_token]);

  const getClientAndUrl = (typeOrDomain, path) => {
    const client = axiosClients[typeOrDomain] || axiosClients.default;
    const baseURL =
      getValueFromMappedObject(domainTypeMap, typeOrDomain) ||
      domainTypeMap.default;
    const url = `${baseURL}/${path}`;
    return { client, url };
  };

  // -----------------------------------------
  // GET Request
  // -----------------------------------------
  const axiosGetRequest = useCallback(
    async ({
      name,
      type = "default",
      path,
      responseType,
      headers,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
      onFailMsg,
      hideFailMsg,
      cancelSource,

      isFile,
      fileDownload,
      fileDownloadName,
      openInNewTab,
    }) => {
      setAxiosGetIsLoading(true);

      const { client, url } = getClientAndUrl(type, path);
      const effectiveResponseType = isFile ? "blob" : responseType || "json";

      logRequest({ method: "GET", name, url });

      try {
        const response = await client.get(`/${path}`, {
          responseType: effectiveResponseType,
          cancelToken: cancelSource?.token,
          headers,
        });

        const responseData = response.data;

        if (isFile) {
          handleFileBlob({
            blob: responseData,
            headers: response.headers,
            fileDownload,
            fileDownloadName,
            openInNewTab,
          });
        }

        logResponse({ method: "GET", name, data: responseData, isFile });

        if (onSuccessFn) onSuccessFn(responseData, response);
        if (onCompleteFn) onCompleteFn(responseData);
      } catch (err) {
        if (axios.isCancel(err)) {
          console.error("CANCEL", err);
          return;
        }

        setAxiosError(err.message);
        logError({ method: "GET", name, err });

        if (!hideFailMsg) {
          enqueueSnackbar(
            onFailMsg
              ? onFailMsg
              : name
                ? `Could not ${name}`
                : "Could not fetch data",
            {
              autoHideDuration: 2800,
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
              content: (key, message) => (
                <CustomSnackbar
                  id={key}
                  message={message}
                  variant="error"
                  closeSnackbar={() => closeSnackbar(key)}
                />
              ),
            },
          );
        }

        if (onCompleteFn) onCompleteFn();
        if (onFailFn) onFailFn(err);
      } finally {
        setAxiosGetIsLoading(false);
      }
    },
    [axiosClients, session_token],
  );

  // -----------------------------------------
  // POST Request
  // -----------------------------------------
  const axiosPostRequest = useCallback(
    async ({
      name,
      path,
      payload = {},
      files,
      onSuccessFn,
      domain = "default",
      onCompleteFn,
      onSuccessMsg,
      onFailFn,
      onFailMsg,
      hideFailMsg,
      cancelToken,
      headers,
      errorTesting = false,
      includeQueryParams,
      formData = false,

      isFile,
      fileDownload,
      fileDownloadName,
      openInNewTab,
      responseType,
    }) => {
      setAxiosPostIsLoading(true);

      try {
        if (isBoolean(includeQueryParams)) {
          if (includeQueryParams) {
            const urlParams = new URLSearchParams(window.location.search);
            payload.query_params = Object.fromEntries(urlParams.entries());
          } else {
            payload.query_params = {};
          }
        }

        let dataToSend = payload;
        const finalHeaders = {
          Authorization: `Bearer ${session_token}`,
          ...headers,
        };

        if (formData) {
          const formDataPayload = new FormData();

          if (isArray(files) && !isEmpty(files)) {
            payload.uploadMetadata = files;
          }

          const uploadMetadata = payload?.uploadMetadata || [];

          uploadMetadata.forEach((fileObject) => {
            if (fileObject.file && fileObject.file instanceof File) {
              formDataPayload.append(
                "uploadFiles",
                fileObject.file,
                fileObject.id,
              );
              delete fileObject.file;
            }
          });

          formDataPayload.append("submission", JSON.stringify(payload));
          dataToSend = formDataPayload;
          finalHeaders["Content-Type"] = "multipart/form-data";
        }

        const type = errorTesting ? "errorTesting" : domain;
        const { client, url } = getClientAndUrl(type, path);

        logRequest({ method: "POST", name, url, payload });

        const response = await client.post(`/${path}`, dataToSend, {
          headers: finalHeaders,
          cancelToken,
          responseType: responseType || (isFile ? "blob" : "json"),
        });

        const responseData = response.data;

        if (isFile) {
          handleFileBlob({
            blob: responseData,
            headers: response.headers,
            fileDownload,
            fileDownloadName,
            openInNewTab,
          });
        }

        logResponse({ method: "POST", name, data: responseData, isFile });

        if (onSuccessMsg) {
          enqueueSnackbar(onSuccessMsg, {
            autoHideDuration: 2800,
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            content: (key, message) => (
              <CustomSnackbar
                id={key}
                message={message}
                variant="success"
                closeSnackbar={() => closeSnackbar(key)}
              />
            ),
          });
        }

        if (onSuccessFn) onSuccessFn(responseData);
      } catch (err) {
        setAxiosError(err.response?.data);
        logError({ method: "POST", name, err });

        if (!hideFailMsg) {
          enqueueSnackbar(
            onFailMsg
              ? onFailMsg
              : name
                ? `Could not ${name}`
                : "Could not submit data",
            {
              autoHideDuration: 2800,
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
              content: (key, message) => (
                <CustomSnackbar
                  id={key}
                  message={message}
                  variant="error"
                  closeSnackbar={() => closeSnackbar(key)}
                />
              ),
            },
          );
        }

        if (onFailFn) onFailFn(err);
      } finally {
        if (onCompleteFn) onCompleteFn();
        setAxiosPostIsLoading(false);
      }
    },
    [axiosClients, session_token],
  );

  // -----------------------------------------
  // SSE Request (POST to start → stream progress → GET result)
  // -----------------------------------------
  const axiosSSERequest = useCallback(
    async ({
      name,
      path,
      payload = {},
      domain = "default",
      headers,
      formData = false,
      files,

      // SSE-specific
      // bearerToken,
      // bearerToken = "69b97e6137782569c074f7aa-foosball",
      onProgressFn,
      getSSEUrl, // (responseData) => string — extract SSE URL from POST response
      getStatusUrl, // (responseData) => string — extract final result URL (optional)

      // Standard callbacks
      onSuccessFn,
      onCompleteFn,
      onProcessCompleteFn,
      onFailFn,
      onFailMsg,
      hideFailMsg,
    }) => {
      setAxiosSSEIsLoading(true);
      setAxiosSSEProgress(null);

      // Used to signal abort to the fetch-based SSE reader
      let sseAbortController = null;

      try {
        // ── Phase 1: POST to kick off the workflow ──
        const effectiveToken = session_token;
        // const effectiveToken = bearerToken || session_token;
        let dataToSend = payload;
        const finalHeaders = {
          Authorization: `Bearer ${effectiveToken}`,
          ...headers,
        };

        if (formData) {
          const formDataPayload = new FormData();

          if (isArray(files) && !isEmpty(files)) {
            payload.uploadMetadata = files;
          }

          const uploadMetadata = payload?.uploadMetadata || [];

          uploadMetadata.forEach((fileObject) => {
            if (fileObject.file && fileObject.file instanceof File) {
              formDataPayload.append(
                "uploadFiles",
                fileObject.file,
                fileObject.id,
              );
              delete fileObject.file;
            }
          });

          formDataPayload.append("submission", JSON.stringify(payload));
          dataToSend = formDataPayload;
          finalHeaders["Content-Type"] = "multipart/form-data";
        }

        const { client, url } = getClientAndUrl(domain, path);

        logRequest({
          method: "POST",
          name: `${name} (SSE start)`,
          url,
          payload,
        });

        const response = await client.post(`/${path}`, dataToSend, {
          headers: finalHeaders,
        });

        const responseData = response.data;

        logResponse({
          method: "POST",
          name: `${name} (SSE start)`,
          data: responseData,
        });

        // ── Phase 2: Subscribe to SSE via fetch (supports Authorization header) ──
        const sseUrl = getSSEUrl
          ? getSSEUrl(responseData)
          : responseData?.sse_url;

        if (!sseUrl) {
          throw new Error("No SSE URL returned from initial POST");
        }

        const baseURL =
          getValueFromMappedObject(domainTypeMap, domain) ||
          domainTypeMap.default;

        const fullSSEUrl = sseUrl.startsWith("http")
          ? sseUrl
          : `${baseURL}${sseUrl.startsWith("/") ? "" : "/"}${sseUrl}`;

        console.groupCollapsed(
          `%cSSE |>| ${name || "(unnamed)"}`,
          LOG_STYLES.GET.out,
        );
        console.log("SSE URL:", fullSSEUrl);
        console.groupEnd();

        // Use fetch instead of EventSource so we can send the Authorization header
        // (EventSource does not support custom headers)
        sseAbortController = new AbortController();

        const sseResponse = await fetch(fullSSEUrl, {
          headers: {
            Authorization: `Bearer ${effectiveToken}`,
            Accept: "text/event-stream",
          },
          signal: sseAbortController.signal,
        });

        if (!sseResponse.ok) {
          throw new Error(
            `SSE connection failed: ${sseResponse.status} ${sseResponse.statusText}`,
          );
        }

        const reader = sseResponse.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        const processSSEEvents = async () => {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              console.groupCollapsed(
                `%cSSE |<| ${name || "(unnamed)"} (STREAM ENDED)`,
                LOG_STYLES.GET.in,
              );
              console.log("SSE stream closed by server");
              console.groupEnd();
              break;
            }

            buffer += decoder.decode(value, { stream: true });

            // Parse SSE frames from buffer
            const lines = buffer.split("\n");
            buffer = lines.pop() || ""; // Keep incomplete line in buffer

            for (const line of lines) {
              // Skip heartbeat comments and empty lines
              if (line.startsWith(":") || line.trim() === "") continue;

              if (line.startsWith("data:")) {
                const dataStr = line.slice(5).trim();
                if (!dataStr) continue;

                try {
                  const progress = JSON.parse(dataStr);

                  setAxiosSSEProgress(progress);
                  if (onProgressFn) onProgressFn(progress);

                  // ── Error from SSE ──
                  if (progress.error) {
                    if (sseAbortController) sseAbortController.abort();

                    logError({
                      method: "GET",
                      name: `${name} (SSE)`,
                      err: new Error(progress.error),
                    });

                    if (!hideFailMsg) {
                      enqueueSnackbar(
                        onFailMsg || progress.error || `${name} failed`,
                        {
                          autoHideDuration: 2800,
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "right",
                          },
                          content: (key, message) => (
                            <CustomSnackbar
                              id={key}
                              message={message}
                              variant="error"
                              closeSnackbar={() => closeSnackbar(key)}
                            />
                          ),
                        },
                      );
                    }

                    if (onFailFn) onFailFn(new Error(progress.error));
                    if (onCompleteFn) onCompleteFn();
                    setAxiosSSEIsLoading(false);
                    return;
                  }

                  // ── Complete ──
                  if (progress.complete) {
                    if (sseAbortController) sseAbortController.abort();

                    console.groupCollapsed(
                      `%cSSE |<| ${name || "(unnamed)"} (COMPLETE)`,
                      LOG_STYLES.GET.in,
                    );
                    console.log("Final SSE payload:", progress);
                    console.groupEnd();

                    if (onProcessCompleteFn) {
                      onProcessCompleteFn(progress);
                    }

                    return;
                  }
                } catch (parseErr) {
                  console.error("SSE parse error:", parseErr, dataStr);
                }
              }
            }
          }
        };

        await processSSEEvents();
      } catch (err) {
        // Ignore abort errors (we triggered them intentionally on complete/error)
        if (err.name === "AbortError") return;

        // Phase 1 POST failed or SSE fetch failed
        setAxiosError(err.response?.data || err.message);
        logError({ method: "POST", name: `${name} (SSE start)`, err });

        if (!hideFailMsg) {
          enqueueSnackbar(
            onFailMsg || `Could not start ${name || "SSE workflow"}`,
            {
              autoHideDuration: 2800,
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
              content: (key, message) => (
                <CustomSnackbar
                  id={key}
                  message={message}
                  variant="error"
                  closeSnackbar={() => closeSnackbar(key)}
                />
              ),
            },
          );
        }

        if (onFailFn) onFailFn(err);
        if (onCompleteFn) onCompleteFn();
        setAxiosSSEIsLoading(false);
      }
    },
    [axiosClients, axiosGetRequest, session_token],
  );

  return {
    axiosPostIsLoading,
    axiosGetIsLoading,
    axiosSSEIsLoading,
    axiosSSEProgress,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
    axiosSSERequest,
  };
};
